<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'
import { getTranslatedProperty, type getListingFilters } from '@shopware/helpers'

type ListingFilter = ReturnType<typeof getListingFilters>[number]

const props = defineProps<{
    listingType?: 'category' | 'search'
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const { public: { isNewPropertyId: IS_NEW_PROPERTY_ID } } = useRuntimeConfig()
const { withOnSalePostFilter, isOnSalePage } = useOnSaleFilter()

const { isSearchListing, getInitialFilters, doSearch } = useListingContext(props.listingType ?? 'category')

// Shared filter state — same reactive object used by ProductListingFilters
const selectedFilters = useListingFilters()
const selectedManufacturerIds = computed(() => [...selectedFilters.manufacturer])
const selectedPropertyIds = computed(() => [...selectedFilters.properties])

const showResetFiltersButton = computed(() =>
    selectedManufacturerIds.value.length > 0 ||
    selectedPropertyIds.value.length > 0 ||
    !!route.query['min-price'] ||
    !!route.query['max-price']
)

const EXCLUDED_CODES = ['tdxcats', 'streamCategories']

const isFilterActive = (filter: ListingFilter): boolean => {
    if (!filter?.code) return false
    if (filter.code === 'manufacturer') return selectedManufacturerIds.value.length > 0
    if (filter.code === 'properties') {
        const groupOptionIds = new Set(filter.options?.map(o => o?.id) || [])
        return selectedPropertyIds.value.some(id => groupOptionIds.has(id))
    }
    if (filter.code === 'price') return !!route.query['min-price'] || !!route.query['max-price']
    return false
}

const getChipLabel = (filter: ListingFilter): string => {
    if (filter.code === 'manufacturer') {
        return selectedManufacturerIds.value
            .map(id => filter.entities?.find(e => e.id === id))
            .filter(Boolean)
            .map(e => getTranslatedProperty(e as { name?: string; translated?: { name?: string } }, 'name'))
            .join(', ')
    }
    if (filter.code === 'properties') {
        const groupOptionIds = new Set(filter.options?.map(o => o?.id) || [])
        const isIsNewFilter = groupOptionIds.has(IS_NEW_PROPERTY_ID)
        if (isIsNewFilter) return t('listing.filter.isNewLabel')
        return selectedPropertyIds.value
            .filter(id => groupOptionIds.has(id))
            .map(id => filter.options?.find(o => o.id === id))
            .filter(Boolean)
            .map(o => getTranslatedProperty(o as { name?: string; translated?: { name?: string } }, 'name'))
            .join(', ')
    }
    if (filter.code === 'price') {
        const min = route.query['min-price']
        const max = route.query['max-price']
        const parts = []
        if (min) parts.push(min)
        if (max) parts.push(max)
        return `${t('listing.filter.priceLabel')}: ${parts.join(' - ')}`
    }
    return ''
}

const clearFilterGroup = async (filter: ListingFilter) => {
    if (!filter?.code) return

    const newQuery = { ...(route.query as Record<string, string>) }

    if (filter.code === 'manufacturer') {
        delete newQuery.manufacturer
    } else if (filter.code === 'properties') {
        const groupOptionIds = new Set(filter.options?.map(o => o?.id) || [])
        const remaining = selectedPropertyIds.value.filter(id => !groupOptionIds.has(id))
        if (remaining.length > 0) {
            newQuery.properties = remaining.join('|')
        } else {
            delete newQuery.properties
        }
        // Remove isNew=1 if this group contains IS_NEW_PROPERTY_ID
        if (groupOptionIds.has(IS_NEW_PROPERTY_ID)) {
            delete newQuery.isNew
        }
    } else if (filter.code === 'price') {
        delete newQuery['min-price']
        delete newQuery['max-price']
    }

    await router.push({ query: newQuery } as RouteLocationRaw)
    await doSearch(withOnSalePostFilter({ ...newQuery, isNew: undefined }))
}

const onResetFilters = async () => {
    const newQuery: Record<string, string | undefined> = {}
    if (route.query.limit) newQuery.limit = route.query.limit as string
    if (route.query.order) newQuery.order = route.query.order as string
    if (isSearchListing.value && route.query.search) newQuery.search = route.query.search as string
    if (isOnSalePage.value) newQuery.isOnSale = '1'

    await router.push({ query: newQuery } as RouteLocationRaw)
    await doSearch(withOnSalePostFilter({ ...newQuery, isNew: undefined }))
}
</script>

<template>
    <div v-if="showResetFiltersButton">
        <span class="text-sm font-bold">{{ $t('listing.filter.activeFiltersLabel') }}</span>
        <template v-for="filter in getInitialFilters" :key="filter?.id || filter?.code">
            <FoundationButton
                v-if="!EXCLUDED_CODES.includes(filter?.code) && isFilterActive(filter)"
                variant="ghost"
                size="small"
                class="inline-flex items-center gap-1.5 bg-base-200 border border-border rounded-full px-3 py-1 text-sm"
                :aria-label="getChipLabel(filter)"
                @click="clearFilterGroup(filter)"
            >
                <span>{{ getChipLabel(filter) }}</span>
                <FoundationIcon name="x" class="w-3.5 h-3.5" />
            </FoundationButton>
        </template>

        <FoundationButton
            variant="ghost"
            size="small"
            class="text-sm font-bold underline ml-1"
            @click="onResetFilters()"
        >
            {{ $t('listing.filter.reset') }}
        </FoundationButton>
    </div>
</template>